import type { Campaign } from "@/interfaces/campaing";
import type { ReportFilter } from "@/interfaces/impactReport";
import type { Product } from "@/interfaces/product";

export type FilterType = "all" | "charity" | "international";
export type CollectionFilterLabels =
  | "All Collections"
  | "All Products"
  | "Charity Only"
  | "International";
export type ProductFilterLabels = "all" | "Charity Gifts" | "Experiences" | "Physical Gifts";
export type SortOption = "price-high-low" | "price-low-high";

export const FILTER_OPTIONS: { value: FilterType; label: CollectionFilterLabels }[] = [
  { value: "all", label: "All Collections" },
  { value: "charity", label: "Charity Only" },
  { value: "international", label: "International" },
];

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "price-high-low", label: "Price: High to Low" },
  { value: "price-low-high", label: "Price: Low to High" },
];

// ─── Product types ─────────────────────────────────────────────────────────────

export type ProductSortOption = "cause" | "category";

export const PRODUCT_FILTER_OPTIONS: { value: ProductFilterLabels; label: ProductFilterLabels }[] =
  [
    { value: "Physical Gifts", label: "Physical Gifts" },
    { value: "Charity Gifts", label: "Charity Gifts" },
    { value: "Experiences", label: "Experiences" },
  ];

export const PRODUCT_SORT_OPTIONS: { value: ProductSortOption; label: string }[] = [
  { value: "cause", label: "All Cause" },
  { value: "category", label: "Category" },
];

// Re-exported from the single status source of truth (`constants/campaings.ts`, `CAMPAIGN_STATUSES`)
// so every consumer that imports it from `@/types/types` traces back to the one constant.
export type { CampaignStatus } from "@/constants/campaings";

export type ClonedEntry = { tempId: string; data: Campaign; loading: boolean };

export type CollectionWithProducts = {
  shopifyId: string;
  type: "domestic" | "international" | "charity";
  role: "base" | "expansion";
  price: number;
  products: Product[];
};

export type BudgetCollectionsResponse = {
  collections: CollectionWithProducts[];
  domesticIndex: Record<number, string>;
};
export type Feature = string | { divider: string };

export type CanvasTab = "email" | "unboxing" | "landing";
export type DeviceMode = "desktop" | "mobile";

export type ConfigTab = "message" | "branding";

export type TextFormat = "bold" | "italic" | "underline";
export type TextAlign = "left" | "center" | "right";

export type TokenKey = "tokenFirstName" | "tokenLastName" | "tokenCompany" | "tokenSignature";

export type EmailMessageSection = "subject" | "message" | "signature";

/* ─── Impact-report filter wire encoding ──────────────────────────────────────────────────────
 *
 * ONE declaration of how every `ReportFilter` field is spelled in the client-impact-report query
 * string, for both directions of the wire. It exists so that OMITTING a field is a COMPILE error
 * rather than a silent runtime drop.
 *
 * What it replaces: the same contract was transcribed into six hand-kept key lists (inbound DTO
 * ×2, outbound Crunchy client ×3, UI client ×3). `as const satisfies ReadonlyArray<keyof
 * ReportFilter>` proved every LISTED key was real — it never proved every REAL key was listed. So
 * a field the DTO accepted but a serializer forgot was quietly dropped from the outbound query,
 * and the caller was served a 200 answering a different question than the one asked.
 *
 * TWO COPIES, ON PURPOSE. This block lives verbatim in BOTH `apps/gfgf-api/src/types/types.ts`
 * and `apps/gfgf-ui/src/types/types.ts`, each declared against that app's own transcription of
 * `interfaces/impactReport.ts#ReportFilter`. The two apps never import from each other, so the
 * copies stay twins by convention — but each is now welded to ITS OWN app's contract, which is
 * the drift that actually bit. The text is deliberately identical in both files so that
 * `diff apps/gfgf-api/src/types/types.ts apps/gfgf-ui/src/types/types.ts` still shows this block
 * as unchanged; if it ever doesn't, the two apps have started to disagree.
 */

/** Every field name the report filter defines. `organizationId` is deliberately not one. */
export type ReportFilterField = keyof ReportFilter;

/**
 * How a single field is spelled in the query string.
 *   scalar    one `key=value` pair
 *   boolean   one `key=true|false` pair — an explicit pair, never truthiness
 *   repeated  one pair PER ELEMENT (`cause=a&cause=b`), never comma-joined (a comma-joined value
 *             would corrupt a cause containing a comma); an empty selection emits NOTHING at all,
 *             never a bare `?orderIds=`, which the inbound DTO rejects with a 400
 */
export type ReportFilterWireEncoding = "scalar" | "boolean" | "repeated";

/**
 * The encoding a field's TYPE demands — derived, never declared, so a field cannot be labelled
 * with an encoding that contradicts how it is actually typed (`cause: "scalar"` does not compile,
 * and neither does moving a field from `string` to `string[]` without re-declaring it). The tuple
 * wrappers stop the conditional distributing over `boolean`'s `true | false`.
 */
type WireEncodingForFilterValue<TValue> = [NonNullable<TValue>] extends [readonly unknown[]]
  ? "repeated"
  : [NonNullable<TValue>] extends [boolean]
    ? "boolean"
    : "scalar";

/**
 * EXHAUSTIVE: exactly one entry per `ReportFilter` field, no more and no fewer. The `-?` is the
 * whole mechanism — it strips the contract's optionality so every key is REQUIRED here. Add a
 * field to `ReportFilter` and `REPORT_FILTER_WIRE_ENCODING` stops compiling until that field
 * declares how it goes on the wire.
 *
 * It is also why `organizationId` can never be serialized: it is not a `ReportFilter` field, so
 * the type forbids it as a key. That allow-list is now structural rather than a habit.
 */
export type ReportFilterWireEncodingMap = {
  [Field in keyof ReportFilter]-?: WireEncodingForFilterValue<ReportFilter[Field]>;
};
